import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    connectItero,
    disconnectItero,
    fetchIteroClinics,
    fetchIteroStatus,
    refreshItero,
    type IteroStatus
} from '../services/iteroConnection';
import { ITERO_CLINICS_KEY, ITERO_STATUS_KEY } from './queryKeys';

function withoutLiveCheck({ live: _live, ...status }: IteroStatus): IteroStatus {
    return status;
}

// Pass enabled=false without practices.itero.get: a forbidden GET redirects the app to /forbidden.
export function useIteroStatus(practiceId: string, enabled = true) {
    return useQuery({
        queryKey: ITERO_STATUS_KEY(practiceId),
        queryFn: () => fetchIteroStatus(practiceId, false),
        enabled: enabled && Boolean(practiceId),
        staleTime: 0,
        retry: false
    });
}

// A mutation, not a query: the live check calls iTero and must only run on click.
export function useTestIteroConnection(practiceId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => fetchIteroStatus(practiceId, true),
        onSuccess: (status) => {
            queryClient.setQueryData(ITERO_STATUS_KEY(practiceId), withoutLiveCheck(status));
        }
    });
}

// gcTime 0 so every modal open re-lists clinics instead of showing a stale pairing state.
export function useIteroClinics(practiceId: string, enabled: boolean) {
    return useQuery({
        queryKey: ITERO_CLINICS_KEY(practiceId),
        queryFn: () => fetchIteroClinics(practiceId),
        enabled: enabled && Boolean(practiceId),
        staleTime: 0,
        gcTime: 0,
        retry: false,
        refetchOnWindowFocus: false
    });
}

// Status only: re-listing clinics would trigger another headless iTero login.
function useInvalidateIteroStatus(practiceId: string) {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: ITERO_STATUS_KEY(practiceId) });
}

export function useConnectItero(practiceId: string) {
    const invalidate = useInvalidateIteroStatus(practiceId);

    return useMutation({
        mutationFn: (clinicId: number) => connectItero(practiceId, clinicId),
        onSettled: invalidate
    });
}

export function useRefreshItero(practiceId: string) {
    const invalidate = useInvalidateIteroStatus(practiceId);

    return useMutation({
        mutationFn: () => refreshItero(practiceId),
        onSettled: invalidate
    });
}

export function useDisconnectItero(practiceId: string) {
    const invalidate = useInvalidateIteroStatus(practiceId);

    return useMutation({
        mutationFn: () => disconnectItero(practiceId),
        onSettled: invalidate
    });
}
